import { computed, unref } from 'vue';
import { useStockRealtimeSnapshot } from '@/common/hooks/useStockRealtimeSnapshot';
import { useStockKlineRealtime } from '@/common/hooks/useStockKlineRealtime';
import { mergeKlineListFromHistory } from '@/components/biz/bizStockCandlestickChart/hooks/applyStockKlineHistory';
import { STOCK_QUOTE_TYPE } from '@/pages/find/stock/constants';
import { formatCustomTime } from '@/utils/api';
import dayjs from 'dayjs';

/** 锚点卡片实时行情订阅字段（与 stockHeaderFullcode 估值区对齐） */
const POPOVER_REALTIME_FIELDS = Object.freeze(['lastPrice', 'changePct', 'totalMv', 'ttm']);
const CANDLE_MODE = 1;

/**
 * 按顺序取第一个有效值，实现「实时快照优先、接口数据兜底」。
 * @param {...unknown} values
 * @returns {unknown|null}
 */
function pickFirstValid(...values) {
  for (const value of values) {
    if (value !== null && typeof value !== 'undefined' && value !== '') {
      return value;
    }
  }
  return null;
}

/**
 * 进宝个股锚点卡片实时行情：顶部报价/估值 + 走势小图最后一根日 K。
 * A/HK/US 均订阅 WebSocket，首包未到时回落静态接口值。
 * 提供 `resolveDisplayPrice` / `resolveDisplayChangePct` 供 `v-format` 指令渲染。
 *
 * @param {{
 *   fullCodeRef: import('vue').MaybeRefOrGetter<string|undefined>,
 *   tradeDailyDataRef: import('vue').Ref<Array>,
 *   staticClosePriceRef: import('vue').MaybeRefOrGetter<unknown>,
 *   staticChangePctRef: import('vue').MaybeRefOrGetter<unknown>,
 *   staticTotalMvRef: import('vue').MaybeRefOrGetter<unknown>,
 *   staticPeTtmRef: import('vue').MaybeRefOrGetter<unknown>
 * }} options
 * @returns {{
 *   resolveDisplayPrice: import('vue').ComputedRef<{ value: unknown, changePct: unknown }>,
 *   resolveDisplayChangePct: import('vue').ComputedRef<{ value: unknown, changePct: unknown }>,
 *   displayTotalMv: import('vue').ComputedRef<unknown>,
 *   displayPeTtm: import('vue').ComputedRef<unknown>
 * }}
 */
export function useCustomStocksPopoverRealtime(options) {
  const { fullCodeRef, tradeDailyDataRef, staticClosePriceRef, staticChangePctRef, staticTotalMvRef, staticPeTtmRef } =
    options;

  /** 当前应订阅实时行情的 fullCode */
  const activeRealtimeFullCodes = computed(() => {
    const fullCode = unref(fullCodeRef);
    return fullCode ? [fullCode] : [];
  });

  const { snapshotsMap } = useStockRealtimeSnapshot(activeRealtimeFullCodes, {
    fields: [...POPOVER_REALTIME_FIELDS]
  });

  /** 当前标的扁平行情快照 */
  const realtimeQuote = computed(() => {
    const fullCode = unref(fullCodeRef);
    return fullCode ? snapshotsMap.value[fullCode]?.quote ?? null : null;
  });

  const displayClosePrice = computed(() =>
    pickFirstValid(realtimeQuote.value?.lastPrice, realtimeQuote.value?.price, unref(staticClosePriceRef))
  );

  const displayChangePct = computed(() => pickFirstValid(realtimeQuote.value?.changePct, unref(staticChangePctRef)));

  const displayTotalMv = computed(() => pickFirstValid(realtimeQuote.value?.totalMv, unref(staticTotalMvRef)));

  const displayPeTtm = computed(() => pickFirstValid(realtimeQuote.value?.ttm, unref(staticPeTtmRef)));

  /** 供 `v-format.price` 消费：实时价优先，色 class 跟随涨跌幅 */
  const resolveDisplayPrice = computed(() => ({
    value: displayClosePrice.value,
    changePct: displayChangePct.value
  }));

  /** 供 `v-format.pct` 消费：实时涨跌幅优先 */
  const resolveDisplayChangePct = computed(() => ({
    value: displayChangePct.value,
    changePct: displayChangePct.value
  }));

  /** 日 K 最后一根 bar 的 stock_kline 订阅参数 */
  const stockKlineSubscribeParam = computed(() => {
    const fullCode = unref(fullCodeRef);
    const list = unref(tradeDailyDataRef) || [];
    if (!fullCode || !list.length) {
      return null;
    }
    const lastBar = list[list.length - 1];
    const timeIndex = lastBar?.timeIndex || lastBar?.date || lastBar?.tradingDay;
    if (!timeIndex) {
      return null;
    }
    return {
      fullCode,
      timeIndex: formatCustomTime(timeIndex, 'YYYY-MM-DD 00:00:00'),
      candlePeriod: STOCK_QUOTE_TYPE.DAILY_K,
      candleMode: CANDLE_MODE
    };
  });

  useStockKlineRealtime(stockKlineSubscribeParam, {
    onPayload: (payload) => {
      const list = unref(tradeDailyDataRef);
      if (!list?.length) {
        return;
      }
      const merged = mergeKlineListFromHistory(list, payload, {
        mergeMode: 'period',
        quoteType: STOCK_QUOTE_TYPE.DAILY_K
      });
      // 合并后写回列表，保留 date 字段供折线图 x 轴使用
      tradeDailyDataRef.value = merged.map((item) => ({
        ...item,
        date: item.date || formatCustomTime(dayjs(item.tradingDay).add(8, 'hour').valueOf(), 'YYYY-MM-DD')
      }));
    }
  });

  return {
    resolveDisplayPrice,
    resolveDisplayChangePct,
    displayTotalMv,
    displayPeTtm
  };
}
